import { useEffect, useState } from "react";
import GetUserRole from "../database/GetUserRole";
import useUserDetails from "./UserDetails";

/** Fetches the current user's role ("admin" | "user") from the backend.
 *  Caches in sessionStorage to avoid repeated calls per page navigation.
 *  Returns { role, isAdmin, loading }.
 */
const ROLE_CACHE_KEY = "userRole";

const useUserRole = () => {
  const userDetails = useUserDetails();
  const [role, setRole] = useState(() => {
    try {
      const cached = sessionStorage.getItem(ROLE_CACHE_KEY);
      return cached || null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(!role);

  useEffect(() => {
    let cancelled = false;
    const userId = userDetails?.userId;
    if (!userId) {
      setRole(null);
      setLoading(false);
      return;
    }
    if (role) {
      setLoading(false);
      return;
    }
    (async () => {
      setLoading(true);
      const result = await GetUserRole(userId);
      if (cancelled) return;
      const resolved = result.role || "user";
      setRole(resolved);
      setLoading(false);
      try {
        sessionStorage.setItem(ROLE_CACHE_KEY, resolved);
      } catch {
        // ignore storage errors
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userDetails?.userId, role]);

  return {
    role: role || "user",
    isAdmin: role === "admin",
    loading,
  };
};

export default useUserRole;
